import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Globe, Languages, Check, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const LANGUAGES = {
  "en": "English",
  "es": "Español (Spanish)",
  "fr": "Français (French)",
  "de": "Deutsch (German)",
  "it": "Italiano (Italian)",
  "pt": "Português (Portuguese)",
  "ru": "Русский (Russian)",
  "zh": "中文 (Chinese)",
  "ja": "日本語 (Japanese)",
  "ko": "한국어 (Korean)",
  "ar": "العربية (Arabic)",
  "hi": "हिन्दी (Hindi)",
  "nl": "Nederlands (Dutch)",
  "pl": "Polski (Polish)",
  "tr": "Türkçe (Turkish)",
  "vi": "Tiếng Việt (Vietnamese)",
  "th": "ไทย (Thai)",
  "id": "Bahasa Indonesia",
  "uk": "Українська (Ukrainian)",
  "cs": "Čeština (Czech)",
  "sv": "Svenska (Swedish)",
  "da": "Dansk (Danish)",
  "fi": "Suomi (Finnish)",
  "no": "Norsk (Norwegian)",
  "el": "Ελληνικά (Greek)",
  "he": "עברית (Hebrew)",
  "ro": "Română (Romanian)",
  "hu": "Magyar (Hungarian)",
  "bg": "Български (Bulgarian)",
  "ms": "Bahasa Melayu (Malay)",
  "fil": "Filipino",
};

const LanguageSettings = ({ onClose }) => {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    preferred_language: "en",
    auto_translate: true,
    show_original: true
  });
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/users/language-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        preferred_language: response.data.preferred_language || "en",
        auto_translate: response.data.auto_translate !== false,
        show_original: response.data.show_original !== false
      });
      setDetectedCountry(response.data.detected_country);
    } catch (error) {
      console.error("Failed to fetch language settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/language-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Language settings saved!");
      onClose?.();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
        <p className="text-sm mt-2">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="language-settings">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-5 h-5 text-blue-500" />
        <h3 className="font-bold text-lg">Language & Translation</h3>
      </div>

      {detectedCountry && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
          <span className="text-blue-600">Detected location: </span>
          <strong>{detectedCountry}</strong>
        </div>
      )}

      {/* Language Selection */}
      <div>
        <label className="text-sm font-bold block mb-2">
          <Languages className="w-4 h-4 inline mr-1" />
          Preferred Language
        </label>
        <select
          value={settings.preferred_language}
          onChange={(e) => setSettings(prev => ({ ...prev, preferred_language: e.target.value }))}
          className="retro-input w-full text-sm"
          data-testid="language-select"
        >
          {Object.entries(LANGUAGES).map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Chat messages will be translated to this language
        </p>
      </div>

      {/* Auto-translate toggle */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100">
          <input
            type="checkbox"
            checked={settings.auto_translate}
            onChange={(e) => setSettings(prev => ({ ...prev, auto_translate: e.target.checked }))}
            className="w-4 h-4 accent-blue-500"
            data-testid="auto-translate-toggle"
          />
          <div>
            <span className="text-sm font-medium">Auto-translate messages</span>
            <p className="text-xs text-gray-500">
              Automatically translate chat messages from other languages
            </p>
          </div>
        </label>

        {settings.auto_translate && (
          <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100 ml-4">
            <input
              type="checkbox"
              checked={settings.show_original}
              onChange={(e) => setSettings(prev => ({ ...prev, show_original: e.target.checked }))}
              className="w-4 h-4 accent-blue-500"
            />
            <div>
              <span className="text-sm font-medium">Show original text</span>
              <p className="text-xs text-gray-500">
                Display original message alongside translation
              </p>
            </div>
          </label>
        )}
      </div>

      {/* Preview */}
      <div className="bg-gray-100 rounded p-3">
        <p className="text-xs font-bold text-gray-600 mb-2">Preview:</p>
        <div className="space-y-1 text-sm">
          <div className="bg-white p-2 rounded">
            <span className="text-blue-600">[User]:</span> Hola, ¿cómo estás?
          </div>
          {settings.auto_translate && (
            <div className="bg-blue-50 p-2 rounded text-blue-800">
              <span className="text-xs text-blue-500">Translated:</span> Hello, how are you?
              {settings.show_original && (
                <span className="text-xs text-gray-400 ml-2">(Hola, ¿cómo estás?)</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={onClose}
          className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
        >
          Cancel
        </button>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-bold flex items-center justify-center gap-1"
          data-testid="save-language-settings"
        >
          <Check className="w-4 h-4" />
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default LanguageSettings;
